import type { Category, CreateCategoryBody } from '@klopotun/contracts';
import { useCallback, useState } from 'react';
import {
  useCategoriesQuery,
  useCategoryArchiveMutation,
  useDeleteCategoryMutation,
  useSaveCategoryMutation,
  type SaveCategoryInput,
} from '../../../entities/category/index.js';
import {
  CategoryForm,
  emptyCategoryFormValues,
  toCategoryFormValues,
} from '../../../features/category-form/index.js';
import { ApiError } from '../../../shared/api/index.js';
import { BackButton } from '../../../shared/telegram/index.js';
import {
  EmptyState,
  ErrorState,
  Fab,
  List,
  ListItem,
  SegmentedControl,
  Sheet,
  Spinner,
  type SegmentedOption,
  type SheetAction,
} from '../../../shared/ui/index.js';
import styles from './categories-section.module.css';

type CategoryScope = 'live' | 'archive';

type CategoryEditor = { mode: 'create' } | { mode: 'edit'; category: Category };

const nextSortOrder = (categories: Category[]): number =>
  categories.length === 0 ? 0 : Math.max(...categories.map((item) => item.sortOrder)) + 1;

const SCOPE_OPTIONS: SegmentedOption[] = [
  { value: 'live', label: 'Активні' },
  { value: 'archive', label: 'Архів' },
];

const NAME_TAKEN_TEXT = 'Категорія з такою назвою вже є. Оберіть іншу назву.';

const RESTORE_NAME_TAKEN_TEXT =
  'Повернути не вдалося: категорію з такою назвою вже створено заново. Перейменуйте одну з них.';

const scopeOf = (value: string): CategoryScope => (value === 'archive' ? 'archive' : 'live');

const isConflict = (error: Error | null): boolean =>
  error instanceof ApiError && error.status === 409;

const titleOf = (category: Category): string =>
  category.emoji === null ? category.name : `${category.emoji} ${category.name}`;

const saveErrorTextOf = (error: Error | null): string | null => {
  if (error === null) {
    return null;
  }

  return isConflict(error) ? NAME_TAKEN_TEXT : error.message;
};

type CategoriesListProps = {
  categories: Category[] | undefined;
  scope: CategoryScope;
  isPending: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  onSelect: (category: Category) => void;
};

const CategoriesList = ({
  categories,
  scope,
  isPending,
  errorMessage,
  onRetry,
  onSelect,
}: CategoriesListProps) => {
  if (errorMessage !== null) {
    return (
      <ErrorState
        title="Не вдалося завантажити категорії"
        message={errorMessage}
        onRetry={onRetry}
      />
    );
  }

  if (isPending || categories === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const shown = categories.filter((category) => category.isArchived === (scope === 'archive'));

  if (shown.length === 0) {
    return (
      <EmptyState
        title={scope === 'archive' ? 'В архіві порожньо' : 'Категорій ще немає'}
        description={
          scope === 'archive'
            ? 'Сюди потрапляють категорії, сховані з переліку вибору.'
            : 'Категорії показують, куди саме йдуть гроші.'
        }
      />
    );
  }

  return (
    <List>
      {shown.map((category) => (
        <ListItem
          key={category.id}
          title={titleOf(category)}
          chevron
          onClick={() => {
            onSelect(category);
          }}
        />
      ))}
    </List>
  );
};

export const CategoriesSection = () => {
  const [scope, setScope] = useState<CategoryScope>('live');
  const [editor, setEditor] = useState<CategoryEditor | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [categoryInUse, setCategoryInUse] = useState<Category | null>(null);
  const categories = useCategoriesQuery({ includeArchived: true });
  const saveCategory = useSaveCategoryMutation();
  const archiveCategory = useCategoryArchiveMutation();
  const deleteCategory = useDeleteCategoryMutation();
  const closeEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const save = (body: CreateCategoryBody): void => {
    const input: SaveCategoryInput =
      editor?.mode === 'edit'
        ? { mode: 'update', id: editor.category.id, body }
        : { mode: 'create', body };

    saveCategory.mutate(input, { onSuccess: closeEditor });
  };

  const toggleArchive = (category: Category): void => {
    archiveCategory.mutate(
      { action: category.isArchived ? 'restore' : 'archive', id: category.id },
      {
        onSuccess: () => {
          setSelectedCategory(null);
          setCategoryInUse(null);
        },
      },
    );
  };

  const remove = (category: Category): void => {
    deleteCategory.mutate(category.id, {
      onSuccess: () => {
        setCategoryToDelete(null);
      },
      onError: (failure) => {
        setCategoryToDelete(null);

        if (isConflict(failure)) {
          setCategoryInUse(category);
        }
      },
    });
  };

  if (editor !== null) {
    const saveErrorText = saveErrorTextOf(saveCategory.error);

    return (
      <div className={styles.editor}>
        <BackButton onBack={closeEditor} />
        <h2 className={styles.subtitle}>
          {editor.mode === 'create' ? 'Нова категорія' : 'Редагування категорії'}
        </h2>

        {saveErrorText !== null && (
          <p className={styles.actionError} role="alert">
            {saveErrorText}
          </p>
        )}

        <CategoryForm
          initialValues={
            editor.mode === 'create'
              ? emptyCategoryFormValues(nextSortOrder(categories.data ?? []))
              : toCategoryFormValues(editor.category)
          }
          busy={saveCategory.isPending}
          onSubmit={save}
          onCancel={closeEditor}
        />
      </div>
    );
  }

  const categoryActions: SheetAction[] =
    selectedCategory === null
      ? []
      : [
          {
            id: 'edit',
            label: 'Змінити',
            onSelect: () => {
              setEditor({ mode: 'edit', category: selectedCategory });
              setSelectedCategory(null);
            },
          },
          {
            id: 'archive',
            label: selectedCategory.isArchived ? 'Повернути з архіву' : 'Сховати з переліку',
            disabled: archiveCategory.isPending,
            onSelect: () => {
              toggleArchive(selectedCategory);
            },
          },
          {
            id: 'delete',
            label: 'Видалити назавжди',
            danger: true,
            onSelect: () => {
              setCategoryToDelete(selectedCategory);
              setSelectedCategory(null);
            },
          },
        ];

  const deleteConfirmation: SheetAction[] =
    categoryToDelete === null
      ? []
      : [
          {
            id: 'confirm-delete',
            label: 'Так, видалити',
            danger: true,
            disabled: deleteCategory.isPending,
            onSelect: () => {
              remove(categoryToDelete);
            },
          },
        ];

  const conflictActions: SheetAction[] =
    categoryInUse === null
      ? []
      : [
          {
            id: 'archive-instead',
            label: 'Сховати з переліку',
            disabled: archiveCategory.isPending,
            onSelect: () => {
              toggleArchive(categoryInUse);
            },
          },
        ];

  const deleteErrorText =
    deleteCategory.error === null || isConflict(deleteCategory.error)
      ? null
      : deleteCategory.error.message;

  const archiveErrorText = isConflict(archiveCategory.error) ? RESTORE_NAME_TAKEN_TEXT : null;

  return (
    <>
      <SegmentedControl
        label="Що показувати"
        name="category-scope"
        options={SCOPE_OPTIONS}
        value={scope}
        hideLabel
        onChange={(value) => {
          setScope(scopeOf(value));
        }}
      />

      {deleteErrorText !== null && (
        <p className={styles.actionError} role="alert">
          {deleteErrorText}
        </p>
      )}

      {archiveErrorText !== null && (
        <p className={styles.actionError} role="alert">
          {archiveErrorText}
        </p>
      )}

      <CategoriesList
        categories={categories.data}
        scope={scope}
        isPending={categories.isPending}
        errorMessage={categories.error === null ? null : categories.error.message}
        onRetry={() => {
          void categories.refetch();
        }}
        onSelect={setSelectedCategory}
      />

      {categories.data !== undefined && (
        <Fab
          label="Додати категорію"
          onClick={() => {
            setEditor({ mode: 'create' });
          }}
        />
      )}

      <Sheet
        open={selectedCategory !== null}
        actions={categoryActions}
        title={selectedCategory === null ? undefined : titleOf(selectedCategory)}
        onClose={() => {
          setSelectedCategory(null);
        }}
      />

      <Sheet
        open={categoryToDelete !== null}
        actions={deleteConfirmation}
        title="Видалити категорію? Це не відкотиться."
        closeLabel="Ні, лишити"
        onClose={() => {
          setCategoryToDelete(null);
        }}
      />

      <Sheet
        open={categoryInUse !== null}
        actions={conflictActions}
        title="Цю категорію використовує підписка, тому видалити її не можна. Її можна сховати з переліку — підписки лишаться цілими."
        closeLabel="Нічого не робити"
        onClose={() => {
          setCategoryInUse(null);
        }}
      />
    </>
  );
};
